import React, { useEffect, useState } from 'react';
import {
  Building2,
  CheckCircle2,
  Clock,
  ArrowUpRight,
  Sparkles,
  DollarSign,
  ExternalLink,
  Cpu,
  Bell
} from 'lucide-react';
import { Button } from '../ui/RfpButton';
import { Badge } from '../ui/RfpBadge';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, PieChart, Pie, Cell } from 'recharts';
import { authedFetch } from '@/lib/authedFetch';

export const Dashboard = ({ onNavigate }) => {
  const [matching, setMatching] = useState(false);
  const [loading, setLoading] = useState(true);
  const apibase = process.env.REACT_APP_API_BASE_URL;
  const [stats, setStats] = useState({
    discovered_count: 0,
    high_match_count: 0,
    total_pipeline_value: 0.0,
    proposals_due_week: 0,
    pie_data: [],
    trend_data: [],
    recent_opportunities: [],
    active_llm: "OpenAI gpt-4o-mini Active"
  });

  const fetchStats = async () => {
    try {
      setLoading(true);
      const res = await authedFetch(`${apibase}/opportunities/stats`, {
        headers: { 'ngrok-skip-browser-warning': 'true' },
      });
      const json = await res.json();
      if (json && json.data) {
        setStats(json.data);
      }
    } catch (e) {
      console.error("Error fetching live dashboard stats:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  const fetchNotifications = async () => {
    try {
      const res = await authedFetch(`${apibase}/opportunities/notifications`, {
        headers: { 'ngrok-skip-browser-warning': 'true' },
      });
      const json = await res.json();
      if (json?.success) {
        setNotifications(json.data || []);
      }
    } catch (e) {
      console.error("Error fetching notifications:", e);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const handleRunMatch = async () => {
    setMatching(true);
    try {
      await authedFetch(`${apibase}/opportunities/trigger-crawl`, {
        method: 'POST',
        headers: { 'ngrok-skip-browser-warning': 'true' },
      });
      await fetchStats();
      if (onNavigate) onNavigate('opportunities');
    } catch (e) {
      console.error(e);
      if (onNavigate) onNavigate('opportunities');
    } finally {
      setMatching(false);
    }
  };

  const formatCurrency = (val) => {
    if (!val) return '$0';
    if (val >= 1000000) {
      return `$${(val / 1000000).toFixed(1)}M`;
    }
    if (val >= 1000) {
      return `$${(val / 1000).toFixed(0)}K`;
    }
    return `$${val.toLocaleString()}`;
  };

  return (
    <div className="space-y-6 p-6">
      {/* Top Banner */}
      <div className="glass-card p-6 rounded-2xl border border-indigo-500/20 bg-gradient-to-r from-indigo-950/40 via-slate-900/60 to-slate-950/80 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-5 h-5 text-indigo-400" />
            <h2 className="text-xl font-bold text-white font-sans">Enterprise Opportunity Intelligence</h2>
            <span className="ml-2 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
              <Cpu className="w-3 h-3 text-indigo-400" />
              {stats.active_llm}
            </span>
          </div>
          <p className="text-sm text-slate-400">
            Monitoring {stats.discovered_count.toLocaleString()}+ Federal, State & Commercial procurement portals backed by live database scoring.
          </p>
        </div>
        <div className="flex gap-3 items-center">
          {/* <div className="relative">
            <button
              onClick={() => setShowNotifications((v) => !v)}
              className="relative p-2.5 rounded-lg bg-slate-800/60 hover:bg-slate-800 border border-slate-700 text-slate-300 hover:text-white transition-colors"
            >
              <Bell className="w-4 h-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            {showNotifications && (
              <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto glass-card rounded-xl border border-slate-800 shadow-xl z-50 p-2 space-y-1">
                {notifications.length === 0 ? (
                  <p className="text-xs text-slate-500 p-3 text-center">No notifications yet.</p>
                ) : (
                  notifications.map((n) => (
                    <div key={n.id} className={`p-3 rounded-lg text-xs space-y-0.5 ${n.is_read ? 'bg-slate-900/40' : 'bg-indigo-500/10 border border-indigo-500/20'}`}>
                      <p className="font-semibold text-white">{n.title}</p>
                      <p className="text-slate-400">{n.message}</p>
                      <p className="text-[10px] text-slate-500">{n.created_at}</p>
                    </div>
                  ))
                )}
              </div>
            )}
          </div> */}
          {/* <Button variant="outline" size="sm" onClick={() => onNavigate && onNavigate('settings')}>
            Configure Crawlers
          </Button> */}
          <Button variant="primary" size="sm" onClick={handleRunMatch} disabled={matching}>
            {matching ? 'Running Match...' : 'Run AI Opportunity Match'}
          </Button>
        </div>
      </div>

      {/* KPI Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <div className="glass-card glass-card-hover p-5 rounded-xl cursor-pointer" onClick={() => onNavigate && onNavigate('opportunities')}>
          <div className="flex justify-between items-start mb-3">
            <span className="p-2.5 rounded-lg bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
              <Building2 className="w-5 h-5" />
            </span>
            <span className="text-xs font-semibold text-emerald-400 flex items-center gap-0.5">
              Live DB <ArrowUpRight className="w-3.5 h-3.5" />
            </span>
          </div>
          <p className="text-2xl font-bold text-white">{stats.discovered_count.toLocaleString()}</p>
          <p className="text-xs text-slate-400 font-medium">Discovered Opportunities</p>
        </div>

        <div className="glass-card glass-card-hover p-5 rounded-xl cursor-pointer" onClick={() => onNavigate && onNavigate('opportunities')}>
          <div className="flex justify-between items-start mb-3">
            <span className="p-2.5 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
              <CheckCircle2 className="w-5 h-5" />
            </span>
            <span className="text-xs font-semibold text-emerald-400 flex items-center gap-0.5">
              Live Scoring <ArrowUpRight className="w-3.5 h-3.5" />
            </span>
          </div>
          <p className="text-2xl font-bold text-white">{stats.high_match_count}</p>
          <p className="text-xs text-slate-400 font-medium">High Match BID Recommendations</p>
        </div>

        <div className="glass-card glass-card-hover p-5 rounded-xl cursor-pointer" onClick={() => onNavigate && onNavigate('opportunities')}>
          <div className="flex justify-between items-start mb-3">
            <span className="p-2.5 rounded-lg bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
              <DollarSign className="w-5 h-5" />
            </span>
            <span className="text-xs font-semibold text-cyan-400">Total Pipeline</span>
          </div>
          <p className="text-2xl font-bold text-white">{formatCurrency(stats.total_pipeline_value)}</p>
          <p className="text-xs text-slate-400 font-medium">Qualified Tender Value</p>
        </div>

        <div className="glass-card glass-card-hover p-5 rounded-xl cursor-pointer" onClick={() => onNavigate && onNavigate('opportunities')}>
          <div className="flex justify-between items-start mb-3">
            <span className="p-2.5 rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/20">
              <Clock className="w-5 h-5" />
            </span>
            <span className="text-xs font-semibold text-amber-400">Due Soon</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats.proposals_due_week}</p>
          <p className="text-xs text-slate-400 font-medium">Proposals Due Soon</p>
        </div>
      </div>

      {/* Analytics Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tender Discovery Trend Chart */}
        <div className="glass-card p-5 rounded-xl lg:col-span-2 space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="font-bold text-base text-white">Discovery & Match Velocity</h3>
              <p className="text-xs text-slate-400">Live breakdown of scraped, AI-matched, and submitted tenders</p>
            </div>
            <Badge variant="outreach">Live Analytics</Badge>
          </div>

          <div className="h-64">
            {stats.trend_data && stats.trend_data.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats.trend_data}>
                  <defs>
                    <linearGradient id="colorDiscovered" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorMatched" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} />
                  <Tooltip contentStyle={{ backgroundColor: '#0f172a', borderColor: '#1e293b' }} />
                  <Area type="monotone" dataKey="discovered" stroke="#6366f1" fillOpacity={1} fill="url(#colorDiscovered)" name="Discovered Tenders" />
                  <Area type="monotone" dataKey="matched" stroke="#10b981" fillOpacity={1} fill="url(#colorMatched)" name="Matched Bids" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-slate-500 text-sm">
                No trend velocity data available. Trigger live crawler to populate.
              </div>
            )}
          </div>
        </div>

        {/* Decision Breakdown Pie Chart */}
        <div className="glass-card p-5 rounded-xl space-y-4 flex flex-col justify-between">
          <div>
            <h3 className="font-bold text-base text-white">Decision Engine Breakdown</h3>
            <p className="text-xs text-slate-400">AI Win probability classification distribution</p>
          </div>

          <div className="h-48 flex justify-center items-center">
            {stats.pie_data && stats.pie_data.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={stats.pie_data} cx="50%" cy="50%" innerRadius={50} outerRadius={70} paddingAngle={4} dataKey="value">
                    {stats.pie_data.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: '#0f172a', borderColor: '#1e293b' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-slate-500 text-sm">No decision records found.</div>
            )}
          </div>

          <div className="space-y-2">
            {stats.pie_data && stats.pie_data.map((item) => (
              <div key={item.name} className="flex justify-between items-center text-xs">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }}></span>
                  <span className="text-slate-300">{item.name}</span>
                </div>
                <span className="font-semibold text-white">{item.value}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* High-Priority Matched Opportunities Table */}
      <div className="glass-card p-5 rounded-xl space-y-4">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="font-bold text-base text-white">High-Priority Matched Opportunities</h3>
            <p className="text-xs text-slate-400">Tenders scored by live OpenAI GPT-4o-mini & matching engine</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => onNavigate && onNavigate('opportunities')}>
            View All Opportunities
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-slate-300">
            <thead className="bg-slate-900/80 text-xs uppercase text-slate-400 border-b border-slate-800">
              <tr>
                <th className="px-4 py-3">Solicitation / Title</th>
                <th className="px-4 py-3">Agency</th>
                <th className="px-4 py-3">Est. Budget</th>
                <th className="px-4 py-3">AI Score</th>
                <th className="px-4 py-3">Win Prob</th>
                <th className="px-4 py-3">Recommendation</th>
                <th className="px-4 py-3">Deadline</th>
                <th className="px-4 py-3">Source Link</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {stats.recent_opportunities && stats.recent_opportunities.length > 0 ? (
                stats.recent_opportunities.map((opp) => (
                  <tr key={opp.id} className="hover:bg-slate-800/40 transition-colors">
                    <td className="px-4 py-3 font-medium text-white">
                      <p className="font-semibold hover:text-indigo-400 cursor-pointer" onClick={() => onNavigate && onNavigate('opportunities')}>{opp.title}</p>
                      <span className="text-xs text-slate-500 font-mono">{opp.solicitation_number}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-300">{opp.agency}</td>
                    <td className="px-4 py-3 font-semibold text-emerald-400">{formatCurrency(opp.estimated_budget)}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 rounded bg-indigo-500/20 text-indigo-300 text-xs font-bold">
                        {opp.match_score} / 100
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-200">{opp.win_probability}</td>
                    <td className="px-4 py-3">
                      <Badge variant={opp.recommendation.toLowerCase()}>
                        {opp.recommendation}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-400">{opp.deadline_date}</td>
                    <td className="px-4 py-3">
                      <a
                        href={opp.source_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-300 text-xs font-medium border border-indigo-500/30 transition-colors"
                      >
                        {opp.source_name || "Official Source"} <ExternalLink className="w-3 h-3 text-indigo-400" />
                      </a>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-center text-slate-400 text-sm">
                    {loading ? "Loading live database opportunities..." : "No opportunities found in database."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
